import {
  ComponentRef,
  Directive,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  ViewContainerRef,
  inject,
} from '@angular/core';
import { EvidenceCitationChipComponent } from './evidence-citation-chip.component';
import { CITATION_PLACEHOLDER_ATTRIBUTE, SECTION_PLACEHOLDER_ATTRIBUTE } from './citation-markdown';
import { EvidenceCitation } from './evidence-citation.model';
import { ReportCodeIdentity, buildInlineCodeCitation } from './inline-code-reference';

/**
 * Renders a report body and projects a citation component into each placeholder (Issue #1773).
 *
 * This exists because of the shape of the problem rather than for elegance. A report body is one
 * sanitized HTML document that must stay structurally intact, and citations have to appear *inside*
 * it — in the middle of a sentence, inside a table cell, inside a list item. Those two requirements
 * cannot both be met by emitting a flat list of HTML fragments and components: a fragment list can
 * only interleave at the top level, and producing it means cutting the document up, which is what
 * created the vulnerability this directive replaced (security review of PR #1824, finding F1).
 *
 * So the document is written once, whole, and the components are attached to placeholder elements
 * afterwards. The only string trusted here is the sanitized document itself — never a slice of it.
 */
@Directive({
  selector: '[appCitationBody]',
})
export class CitationBodyDirective implements OnChanges, OnDestroy {
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly viewContainer = inject(ViewContainerRef);

  private chips: ComponentRef<EvidenceCitationChipComponent>[] = [];

  /** Signature of the inputs the current render was built from. Empty before the first render. */
  private renderedSignature = '';

  /**
   * Sanitized report HTML carrying citation placeholders.
   *
   * Assigned to `innerHTML` directly rather than through Angular's `[innerHTML]` binding. Angular
   * would sanitize it again and strip the placeholders' `data-` attribute, and this module has
   * already sanitized it against a policy stricter than Angular's own — see
   * `sanitizeReportMarkdown` in `citation-markdown.ts`.
   */
  @Input({ required: true }) appCitationBody = '';

  /** Resolved citations, keyed by the ID its placeholder names. */
  @Input({ required: true }) citations: ReadonlyMap<string, EvidenceCitation> = new Map();

  /** One-based marker number per citation ID, so inline markers match the sources list. */
  @Input({ required: true }) markerIndexById: ReadonlyMap<string, number> = new Map();

  /** File whose report is open, used only to decide whether a citation leads elsewhere. */
  @Input() activeFileId = '';

  /** Page whose report is open, used only to decide whether a citation leads elsewhere. */
  @Input() activePageNumber = 0;

  /**
   * Catalog identity of the report being rendered, for inline code-section links (Issue #1773).
   *
   * Comes from the structured payload — the code book and section hierarchy — and never from the
   * statement text. Everything that reaches an inline link's URL is taken from here; the prose
   * contributes only a section number. Absent identity means no inline links, which is why this is
   * optional rather than required: a report that cannot be linked still has to render.
   */
  @Input() reportCode: ReportCodeIdentity | null = null;

  /**
   * Section titles from the table of contents the compliance page already loaded, keyed by number.
   *
   * Empty is a valid state and produces number-only tooltips; it is what a report rendered before
   * the contents arrive, or one naming subsections the loaded contents do not include, will show.
   */
  @Input() sectionTitles: ReadonlyMap<string, string> = new Map();

  /**
   * Re-renders only when an input actually changed in value.
   *
   * The second line of defence, and it exists because the first one failed in a way nothing caught.
   * The report tab bound `[reportCode]="reportCode()"`, a method returning a new object literal on
   * every change-detection cycle; Angular compares object inputs by reference, so `ngOnChanges`
   * fired every cycle and this directive destroyed and rebuilt every chip each time. On the deployed
   * build the inline anchor was replaced in eight of ten samples over three seconds, which meant a
   * reader's click could land on an element that no longer existed.
   *
   * Comparing by value here means a caller passing an equivalent-but-new object costs nothing. The
   * tab now passes stable references anyway; this makes the directive robust to the next caller that
   * does not, because the failure mode is invisible to every test that renders once.
   */
  ngOnChanges(): void {
    const signature = this.inputSignature();
    if (signature === this.renderedSignature) return;
    this.renderedSignature = signature;
    this.render();
  }

  /**
   * A value-equality summary of everything a render depends on.
   *
   * The citation maps are summarised by their keys rather than compared deeply: the resolver builds
   * them together with the body, so a changed body is what a changed citation set looks like from
   * here, and a deep comparison would cost more than the render it is avoiding.
   */
  private inputSignature(): string {
    const code = this.reportCode;
    return JSON.stringify([
      this.appCitationBody,
      [...this.citations.keys()],
      [...this.markerIndexById.entries()],
      this.activeFileId,
      this.activePageNumber,
      code && [code.bookId, code.documentSlug, code.chapterSlug, code.chapterTitle, code.sectionId],
      // Titles change what the links SAY, so a late-arriving table of contents has to re-render.
      // Omitting them here would leave the first render's number-only tooltips in place for good.
      [...this.sectionTitles.entries()],
    ]);
  }

  ngOnDestroy(): void {
    this.destroyChips();
  }

  /**
   * Writes the body and attaches a chip to every placeholder.
   *
   * Chips are destroyed and rebuilt on each change rather than diffed. A report body is replaced
   * wholesale when the reader selects a different section, so there is nothing to reconcile, and
   * leaving stale components attached to elements that no longer exist is how this kind of code
   * leaks views.
   */
  private render(): void {
    this.destroyChips();

    const element = this.host.nativeElement;
    element.innerHTML = this.appCitationBody;
    if (!this.appCitationBody) return;

    this.attachSectionLinks(element);

    const placeholders = element.querySelectorAll<HTMLElement>(`[${CITATION_PLACEHOLDER_ATTRIBUTE}]`);
    placeholders.forEach(placeholder => {
      const citationId = placeholder.getAttribute(CITATION_PLACEHOLDER_ATTRIBUTE) ?? '';
      const citation = this.citations.get(citationId);
      // A placeholder with no matching citation should be impossible, because the renderer only
      // creates one for an ID it has already resolved. Skipping rather than throwing keeps a
      // resolver/renderer disagreement from blanking the whole report.
      if (!citation) return;

      const chip = this.viewContainer.createComponent(EvidenceCitationChipComponent);
      chip.setInput('citation', citation);
      chip.setInput('markerIndex', this.markerIndexById.get(citationId) ?? 1);
      chip.setInput('activeFileId', this.activeFileId);
      chip.setInput('activePageNumber', this.activePageNumber);
      // createComponent anchors the host element beside this directive; moving it into the
      // placeholder puts the citation where the prose actually referenced it. The component stays
      // attached to the view, so change detection still reaches it.
      placeholder.appendChild(chip.location.nativeElement);
      chip.changeDetectorRef.detectChanges();
      this.chips.push(chip);
    });
  }

  /**
   * Turns each inline code-section placeholder into a link, or leaves its number as plain text.
   *
   * The decision is made by {@link buildInlineCodeCitation}, which refuses anything it cannot
   * validate against the report's own catalog identity. A refusal is not an error state here and gets
   * no visible marking: the placeholder already contains the section number as text, so leaving it
   * alone renders exactly the sentence the report wrote. That is deliberately unlike an unresolved
   * evidence marker, which does show itself — a missing *source* is something a reader needs told
   * about, whereas an unlinked cross-reference is just prose.
   */
  private attachSectionLinks(element: HTMLElement): void {
    const placeholders = element.querySelectorAll<HTMLElement>(`[${SECTION_PLACEHOLDER_ATTRIBUTE}]`);
    placeholders.forEach(placeholder => {
      const sectionNumber = placeholder.getAttribute(SECTION_PLACEHOLDER_ATTRIBUTE) ?? '';
      const citation = buildInlineCodeCitation(sectionNumber, this.reportCode, this.sectionTitles.get(sectionNumber));
      if (!citation) return;

      const chip = this.viewContainer.createComponent(EvidenceCitationChipComponent);
      chip.setInput('citation', citation);
      chip.setInput('display', 'inline-text');
      chip.setInput('activeFileId', this.activeFileId);
      chip.setInput('activePageNumber', this.activePageNumber);
      // The number was placeholder text so the reference stays readable if nothing links it. Now
      // that something has, the component owns the wording and the duplicate has to go.
      placeholder.textContent = '';
      placeholder.appendChild(chip.location.nativeElement);
      chip.changeDetectorRef.detectChanges();
      this.chips.push(chip);
    });
  }

  private destroyChips(): void {
    this.chips.forEach(chip => chip.destroy());
    this.chips = [];
  }
}
